import Vue from 'vue';
import i18n  from '@/i18n.js';

const customPropNames = {
  hourseOfOperationSetting: 'hoursOfOperationSetting'
};

import { camelCaseFormat } from '@/core/filters/camelCaseFormat';

/**
 * Validates IVR tree and modifies nodes to add node errors 
 * @param {IVRTree} ivrTree IVR tree which needs to be validated
 * @param {NodesValidationDetails} nodesMapping object which is a map of NodeTypeId to the corresponding validation details
 */
export function validateIVRTree(ivrTree, nodesMapping) {
  ivrTree.nodes.forEach(node => {

    Vue.set(node, 'nodeErrors', validateNode(node, nodesMapping[node.nodeTypeId]));
  });
}

/**
 * Validate any type of node
 * @param {IVRNode} node The IVR node which needs to be validated
 * @param {NodeTypeValidationDetails} nodeTypeDetails Node type validation details
 * @param {Array<import('../ivr').NodeLink>} links
 * @returns {Object} Specific property errors and general node errors
 */
export function validateNode(node, nodeTypeDetails, links = []) {
  if (!nodeTypeDetails) {
    const propertyErrors = [];
    const generalErrors = [];
    if (node.nodeType === 'Unavailable') {
      generalErrors.push(i18n.t('CALL_FLOW_ERROR_UNAVAILABLE'));
    }
    const errors = {
      errors: generalErrors,
      isSuccessful: generalErrors.length ? false : true,
      nodeId: node.id,
      propertyErrors: propertyErrors
    };

    return errors;
  }
  const settings = node.settings;

  const propertyErrors = validateParameters(settings, nodeTypeDetails);
  const generalErrors = [];

  const isInputActive = node.inputs?.some((i) => i.active === true);
  if ( !isInputActive && node.id !== 'root') {
    generalErrors.push(i18n.t('CALL_FLOW_ERROR_INACCESSIBLE'));
  }

  if (!nodeTypeDetails.canBeLeaf && !node.hasChild && node.id !== 'root') {
    generalErrors.push(i18n.t('CALL_FLOW_ERROR_MISSING'));
  }

  if (node.nodeTypeId === 'RouterNode' || node.nodeTypeId == 'SplitTestNode') {
    links.forEach(link => {
      const linkValidationErrors = validateLinkCriterias(link);

      if (!linkValidationErrors.isSuccessful) {
        if (!generalErrors.some((e) => e === i18n.t('CALL_FLOW_ERROR_ROUTES'))) generalErrors.push(i18n.t('CALL_FLOW_ERROR_ROUTES'));
      }
    });
  }

  // Validate MenuNode keyword routes in voice response mode
  if (node.nodeTypeId === 'MenuNode' && settings?.voiceResponse) {
    const successLinks = links.filter(link => link.onSuccess);

    const hasEmptyKeyword = successLinks.some(link => {
      const keywordValue = link.criteria?.[0]?.tagCriteria?.[0]?.value;
      return !keywordValue || (typeof keywordValue === 'string' && !keywordValue.trim());
    });
    if (hasEmptyKeyword) {
      generalErrors.push(i18n.t('CALL_FLOW_ERROR_KEYWORD_REQUIRED'));
    }

    // Check for non-alphabetic characters in keywords (letters and spaces allowed)
    const hasInvalidKeyword = successLinks.some(link => {
      const keywordValue = link.criteria?.[0]?.tagCriteria?.[0]?.value;
      return typeof keywordValue === 'string' && keywordValue.trim() && !/^[a-zA-Z ]+$/.test(keywordValue.trim());
    });
    if (hasInvalidKeyword) {
      generalErrors.push(i18n.t('CALL_FLOW_ERROR_KEYWORD_ALPHA_ONLY'));
    }

    // Check for duplicate keywords
    const keywords = successLinks
      .map(link => {
        const keywordValue = link.criteria?.[0]?.tagCriteria?.[0]?.value;
        return typeof keywordValue === 'string' ? keywordValue.trim().toLowerCase() : '';
      })
      .filter(k => k); // Filter out empty strings
    const uniqueKeywords = new Set(keywords);
    if (keywords.length !== uniqueKeywords.size) {
      generalErrors.push(i18n.t('CALL_FLOW_ERROR_DUPLICATE_KEYWORD'));
    }
  }

  const errors = {
    errors: generalErrors,
    isSuccessful: (generalErrors.length || propertyErrors.length) ? false : true,
    nodeId: node.id,
    propertyErrors
  };

  return errors;
}


/**
 * Validates criterias of a link
 * @param {import('../ivr').NodeLink} link 
 * @returns {{ isSuccessful: boolean }}
 */
export function validateLinkCriterias(link) {
  if (!link || !link.criteria?.length) {
    return {
      isSuccessful: true
    };
  }

  if (link.originNode?.nodeTypeId == 'SplitTestNode') {
    const valid = link.weight > 0 && link.weight < 999 && link.name && link.criteria;
    return { isSuccessful: valid };
  }

  return link.criteria.reduce((validation, criteria) => {
    const invalid = criteria.tagCriteria.some(tc => {
      return (!tc.value && tc.comparisonType != 'EXISTS') || !tc.tagId;
    });

    if (invalid) {
      validation.isSuccessful = !invalid;
    } else if (validation.isSuccessful == null) {
      validation.isSuccessful = true;
    }

    return validation;
  }, {
    isSuccessful: true
  });
}


/**
 * 
 * @param {NodeValues} settings The settings set by the user for a node
 * @param {NodeTypeValidationDetails} parametersValidationFields Node type validation details
 * @param {string} [subGroup] The sub_type the parameters belong to
 * @returns {Array.<Object>} Array of property errors
 */
function validateParameters(settings, parametersValidationFields, subGroup = '') {
  let propertyErrors = [];
  const groups = {};

  Object.keys(parametersValidationFields).forEach(param => {
    if (!parametersValidationFields[param]?.isParameter) {
      return;
    }

    const groupName = parametersValidationFields[param].group;

    if (groupName) {
      groups[groupName] = groups[groupName] || {};
    }

    propertyErrors = [
      ...propertyErrors,
      ...validateParameter(param, parametersValidationFields[param], settings?.[param], settings?.[param]?.isEnabled, subGroup, groups)
    ];
  });

  Object.keys(groups).forEach(group => {
    if (groups[group].isValueHit == 0) {
      propertyErrors.push({
        propertyName: `${groups[group].key}.${group}`,
        errorMessage: `the group ${groups[group].key}.${group} must have valid value`
      });
    }
  });

  return propertyErrors;
}

/**
 * 
 * @param {string} parameterName The parameter of a node which needs to be validated
 * @param {NodeTypeValidationDetails} parameterValidationFields Node type validation details 
 * @param {*} parameterValue The value of a parameter
 * @param {bool} isEnabled If it's a sub_type parameter, we should check if it's enabled rather than the value
 * @param {string} [subGroupParentName] 
 * @param {Object} groupsValidation This is the object which has the result of validating different field groups like `Message` in `MenuNode`
 * @returns {Array.<Object>} Array of property errors
 */
function validateParameter(parameterName, parameterValidationFields, parameterValue, isEnabled, subGroupParentName = '', groupsValidation) {
  let errors = [];
  let parameterNameFinal = parameterName;

  if (customPropNames[parameterName]) {
    parameterNameFinal = customPropNames[parameterName];
  }

  const propertyName = subGroupParentName ? `${subGroupParentName}.${parameterNameFinal}` : parameterNameFinal;
  const displayName = subGroupParentName ? `${camelCaseFormat(subGroupParentName)} > ${parameterValidationFields.displayName}` : parameterValidationFields.displayName;
  const errorDisplayName = displayName ? displayName : camelCaseFormat(propertyName);

  if (parameterValidationFields.required && (parameterValue == null || parameterValue == '')) {
    errors.push({
      displayName: errorDisplayName,
      propertyName,
      errorMessage: 'must have value'
    });
  }

  if (parameterValidationFields.type === 'number'
    && parameterValue != null
    && (parameterValue < parameterValidationFields.min || parameterValue > parameterValidationFields.max)) {
    errors.push({
      displayName: errorDisplayName,
      propertyName,
      errorMessage: 'number out of range'
    });
  }

  if (parameterValidationFields.group && parameterValidationFields.groupRequired) {
    groupsValidation[parameterValidationFields.group] = groupsValidation[parameterValidationFields.group] || {};

    const isValueHit = groupsValidation[parameterValidationFields.group].isValueHit || 0;

    groupsValidation[parameterValidationFields.group].key = subGroupParentName;

    groupsValidation[parameterValidationFields.group].isValueHit = !parameterValue ? isValueHit : isValueHit + 1;
  }

  if (parameterValidationFields.isSubType && parameterValue && (parameterValidationFields.required || isEnabled == null || isEnabled)) {
    const subGroup = parameterName;


    errors = [
      ...errors,
      ...validateParameters(parameterValue, parameterValidationFields, subGroup)
    ];
  }

  return errors;
}

/**
 * Recursive method that given a list of parameters it will return the fields that they must validate
 * @param {*} parameters list of parameters to validate
 * @returns the validation fields of the parameters
 */
export function getParameterValidationFields(parameters) {
  let validationDetails = {};

  parameters?.forEach(param => {
    let subTypeFields = {};
    
    const fieldKey = param.name;

    const isSubType = param.type === 'sub_type';

    if (isSubType) {
      subTypeFields = getParameterValidationFields(param.parameters);
    }

    const parameter = {
      required: !param.isOptional,
      group: param.group,
      groupRequired: param.group && !param.groupOptional,
      isSubType,
      isParameter: true,
      min: param.min,
      max: param.max,
      type: param.type,
      displayName: param.displayName,
      ...subTypeFields
    };

    validationDetails = {
      ...validationDetails,
      [fieldKey]: parameter
    };
  });
  return validationDetails;
}
